import React, { useState } from 'react';
import {
  Col,
  Form,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import './ResetPassword.scss';
import Icon from '@mdi/react';
import { mdiEyeOffOutline, mdiEyeOutline } from '@mdi/js';
import { useMutation } from '@tanstack/react-query';
import { initialValues, validationSchema } from './constants';
import { changeUserPassword } from '../../../store/apis/adminService';
import { encode } from '../../../utils/encrypt.util';
import keyIcon from '../../../assets/icons/passwordKeyGray.png';

function ResetPassword({ isOpen, toggle, user }) {
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onChange',
    defaultValue: initialValues,
    resolver: yupResolver(validationSchema),
  });

  const { mutate: changePassword, isPending } = useMutation({
    mutationFn: changeUserPassword,
    onSuccess: () => {
      toast.success('Password Changed Successfully!', {
        className: 'toast-success',
      });
      reset();
      toggle();
    },
    onError: error => {
      toast.error(error?.message || 'Failed to change password', {
        className: 'toast-error',
      });
    },
  });

  const onSubmit = data => {
    if (!user?.ID || !user?.Name) {
      toast.error('No user selected', {
        className: 'toast-error',
      });
      return;
    }
    changePassword({
      userId: user?.ID,
      userName: user?.Name,
      password: encode(data.password),
      newPassword: encode(data.newPassword),
    });
  };

  return (
    <Offcanvas
      className="change-password-oc"
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
    >
      <OffcanvasHeader className="change-password-oc-header">
        Change Password
      </OffcanvasHeader>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <OffcanvasBody className="change-password-oc-body">
          {user && (
            <div className="mb-3 p-2 mt-3 bg-light rounded border">
              <div>
                <strong>User :</strong>{' '}
                {`${user?.UserFullName} (${user.Name})` || 'N/A'}
              </div>
              {user?.Email && (
                <div>
                  <strong>Email :</strong> {user.Email}
                </div>
              )}
              {/* Add more fields as needed */}
            </div>
          )}
          <div className="d-flex flex-column gap-3">
            <div>
              <Label>
                Current Password<span className="text-danger">*</span>
              </Label>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <div className="input-icon-group">
                    <img
                      className="prefix-icon"
                      width={18}
                      height={18}
                      src={keyIcon}
                      alt="key"
                    />
                    <Input
                      {...field}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter Current Password"
                      className={`form-control input-icon external-filter-input ${errors.password && !field.value ? 'is-invalid' : ''}`}
                    />
                    <button
                      type="button"
                      className={`suffix-icon-btn ${errors.password && !field.value ? 'd-none' : ''}`}
                      onClick={togglePasswordVisibility}
                    >
                      <Icon
                        path={showPassword ? mdiEyeOffOutline : mdiEyeOutline}
                        size={0.7}
                      />
                    </button>
                  </div>
                )}
              />
              {errors?.password && (
                <div className="invalid-feedback d-block">
                  {errors.password.message}
                </div>
              )}
            </div>
            <div>
              <Label>
                New Password<span className="text-danger">*</span>
              </Label>
              <Controller
                name="newPassword"
                control={control}
                render={({ field }) => (
                  <div className="input-icon-group">
                    <img
                      className="prefix-icon"
                      width={18}
                      height={18}
                      src={keyIcon}
                      alt="key"
                    />
                    <Input
                      {...field}
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="Enter New Password"
                      className={`form-control external-filter-input  ${errors.newPassword && !field.value ? 'is-invalid' : ''} input-icon`}
                    />
                    <button
                      type="button"
                      className={`suffix-icon-btn ${errors.newPassword && !field.value ? 'd-none' : ''}`}
                      onClick={toggleNewPasswordVisibility}
                    >
                      <Icon
                        path={
                          showNewPassword ? mdiEyeOffOutline : mdiEyeOutline
                        }
                        size={0.7}
                      />
                    </button>
                  </div>
                )}
              />
              {errors?.newPassword && (
                <div className="invalid-feedback d-block">
                  {errors?.newPassword.message}
                </div>
              )}
            </div>
            <div>
              <Label>
                Confirm Password<span className="text-danger">*</span>
              </Label>
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field }) => (
                  <div className="input-icon-group">
                    <img
                      className="prefix-icon"
                      width={18}
                      height={18}
                      src={keyIcon}
                      alt="key"
                    />
                    <Input
                      {...field}
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Enter Confirm Password"
                      className={`form-control external-filter-input ${errors.confirmPassword && !field.value ? 'is-invalid' : ''} input-icon`}
                    />
                    <button
                      type="button"
                      className={`suffix-icon-btn ${errors.confirmPassword && !field.value ? 'd-none' : ''}`}
                      onClick={toggleConfirmPasswordVisibility}
                    >
                      <Icon
                        path={
                          showConfirmPassword ? mdiEyeOffOutline : mdiEyeOutline
                        }
                        size={0.7}
                      />
                    </button>
                  </div>
                )}
              />
              {errors.confirmPassword && (
                <div className="invalid-feedback d-block">
                  {errors.confirmPassword.message}
                </div>
              )}
            </div>
          </div>
        </OffcanvasBody>
        <div className="change-pw-footer p-3 d-flex flex-row align-items-center gap-2">
          <Col>
            <button
              type="button"
              className="w-100 change-pw-cancel"
              onClick={() => {
                toggle();
                reset();
              }}
            >
              Cancel
            </button>
          </Col>
          <Col>
            <button
              type="submit"
              disabled={!isValid || isPending}
              className="w-100 change-pw-change"
            >
              {isPending ? 'Changing...' : 'Change'}
            </button>
          </Col>
        </div>
      </Form>
    </Offcanvas>
  );
}

export default ResetPassword;
